import { motion } from "framer-motion";
import { ArrowRight, Play, Check, CalendarDays, Clock3 } from "lucide-react";
import { PAYMENT_URL, scrollToHash } from "../lib/site";
import { CtaButton, EASE, GradientText } from "./Reveal";

const LINES = [
  <span key="l1">Build Your First</span>,
  <GradientText key="l2">Digital Product</GradientText>,
  <span key="l3">Business Using AI.</span>,
];

const TRUST = ["2 Live Sessions", "Beginner Friendly", "Practical AI Training", "Live Q&A"];

export default function Hero() {
  return (
    <section id="overview" data-testid="hero-section" className="relative overflow-hidden bg-white">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(70%_60%_at_50%_0%,#F6F1FF_0%,#FFFFFF_70%)]" />
      <div className="pointer-events-none absolute -right-40 top-24 h-96 w-96 rounded-full bg-[#9B5CF0]/10 blur-3xl" />
      <div className="pointer-events-none absolute -left-40 top-40 h-80 w-80 rounded-full bg-[#7C3AED]/10 blur-3xl" />
      <div className="relative mx-auto flex max-w-3xl flex-col items-center px-5 pb-20 pt-32 text-center sm:px-8 lg:pb-28 lg:pt-40">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: EASE }}
          className="inline-flex items-center gap-2 rounded-full border border-[#E9DFFC] bg-white/80 px-4 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.22em] text-[#7C3AED] shadow-sm"
          data-testid="hero-eyebrow"
        >
          <span className="relative flex h-1.5 w-1.5">
            <span className="ping-soft absolute inline-flex h-full w-full rounded-full bg-[#7C3AED]" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#7C3AED]" />
          </span>
          2 Days • Live AI Bootcamp
        </motion.div>

        <h1 data-testid="hero-headline" className="mt-7 text-4xl font-extrabold leading-[1.08] tracking-tight text-[#171321] sm:text-5xl lg:text-[3.6rem]">
          {LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.15 + i * 0.13, ease: EASE }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.6, ease: EASE }}
          className="mx-auto mt-6 max-w-lg text-base leading-relaxed text-[#777181] sm:text-lg"
          data-testid="hero-subheadline"
        >
          Learn how to create digital products, launch your first offer and understand online marketing through two practical live sessions.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.72, ease: EASE }}
          className="mt-7 flex flex-wrap items-center justify-center gap-3"
          data-testid="hero-pricing"
        >
          <span className="text-lg font-semibold text-[#B8B1C8] line-through">₹999</span>
          <span className="text-4xl font-extrabold tracking-tight text-[#171321]">₹199</span>
          <span className="rounded-full bg-[#F6F1FF] px-3 py-1 font-mono text-[11px] font-bold uppercase tracking-widest text-[#7C3AED]">
            80% OFF
          </span>
          <span className="w-full text-center text-xs font-medium text-[#777181] sm:w-auto">
            One-Time Registration
          </span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.84, ease: EASE }}
          className="mt-8 flex w-full flex-col items-center gap-3 sm:w-auto sm:flex-row sm:justify-center"
        >
          <CtaButton href={PAYMENT_URL} testId="hero-primary-cta" className="w-full sm:w-auto">
            Reserve My Seat — ₹199 <ArrowRight size={17} strokeWidth={2.5} />
          </CtaButton>
          <a
            href="#preview"
            onClick={(e) => scrollToHash(e, "#preview")}
            data-testid="hero-preview-cta"
            className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#E9DFFC] bg-white px-7 py-3.5 text-[15px] font-semibold text-[#171321] shadow-sm transition-[background-color,border-color,transform] duration-200 hover:border-[#7C3AED]/40 hover:bg-[#F6F1FF] sm:w-auto"
          >
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#7C3AED] text-white">
              <Play size={11} fill="currentColor" />
            </span>
            Watch Bootcamp Preview
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.95, ease: EASE }}
          className="mt-6 inline-flex items-center gap-2.5 rounded-2xl border border-[#E9DFFC] bg-white/80 px-4 py-2.5 shadow-sm"
          data-testid="hero-date-badge"
        >
          <CalendarDays size={15} className="text-[#7C3AED]" />
          <span className="text-[13px] font-bold text-[#171321]">25–26 October 2026</span>
          <span className="h-3 w-px bg-[#E9DFFC]" />
          <Clock3 size={15} className="text-[#7C3AED]" />
          <span className="text-[13px] font-bold text-[#171321]">8:00 PM IST</span>
        </motion.div>

        <motion.ul
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.05 }}
          className="mt-9 flex flex-wrap items-center justify-center gap-x-7 gap-y-3"
          data-testid="hero-trust-highlights"
        >
          {TRUST.map((t) => (
            <li key={t} className="flex items-center gap-2 text-[13px] font-semibold text-[#171321]">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#F6F1FF] text-[#7C3AED]">
                <Check size={12} strokeWidth={3} />
              </span>
              {t}
            </li>
          ))}
        </motion.ul>
      </div>
    </section>
  );
}
